import React, { useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const DRAWER_WIDTH = 240;

const NAV_SECTIONS = [
  {
    label: "Analytics",
    items: [
      { to: "/", icon: <DashboardOutlinedIcon sx={{ fontSize: 18 }} />, label: "Overview" },
      { to: "/reports", icon: <BarChartOutlinedIcon sx={{ fontSize: 18 }} />, label: "Report builder" },
      { to: "/integrations", icon: <HubOutlinedIcon sx={{ fontSize: 18 }} />, label: "Data sources" },
    ],
  },
  {
    label: "Coming soon",
    items: [
      { to: "/exports", icon: <StorageOutlinedIcon sx={{ fontSize: 18 }} />, label: "Destinations", soon: true },
      { to: "/schedules", icon: <ScheduleOutlinedIcon sx={{ fontSize: 18 }} />, label: "Schedules", soon: true },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/settings", icon: <SettingsOutlinedIcon sx={{ fontSize: 18 }} />, label: "Settings", soon: true },
    ],
  },
];

function NavList({ pathname, onNavigate }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Brand */}
      <Box sx={{ px: 3, pt: 3, pb: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "9px",
              background: "linear-gradient(135deg, #818CF8 0%, #5B5BD6 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: "0 2px 8px rgba(91,91,214,0.4)",
            }}
          >
            <BarChartOutlinedIcon sx={{ fontSize: 18, color: "#fff" }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.975rem",
                color: "#F1F5F9",
                letterSpacing: "-0.01em",
                lineHeight: 1.2,
              }}
            >
              OrkaAnalytics
            </Typography>
            <Typography sx={{ fontSize: "0.65rem", color: "#64748B", letterSpacing: "0.02em" }}>
              Marketing platform
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.06)", mx: 2 }} />

      {/* Nav */}
      <Box sx={{ flex: 1, overflowY: "auto", py: 1.5 }}>
        {NAV_SECTIONS.map((section) => (
          <Box key={section.label} sx={{ mb: 0.5 }}>
            <Typography
              sx={{
                display: "block",
                px: 3,
                py: 0.75,
                fontSize: "0.65rem",
                fontWeight: 700,
                letterSpacing: "0.09em",
                textTransform: "uppercase",
                color: "#475569",
              }}
            >
              {section.label}
            </Typography>
            <List disablePadding dense>
              {section.items.map((item) => {
                const selected = item.to === "/"
                  ? pathname === "/"
                  : pathname.startsWith(item.to);
                return (
                  <ListItem key={item.to} disablePadding sx={{ px: 1 }}>
                    <ListItemButton
                      component={RouterLink}
                      to={item.to}
                      selected={selected}
                      onClick={onNavigate}
                      disabled={item.soon}
                      sx={{ borderRadius: 2, py: 0.875 }}
                    >
                      <ListItemIcon sx={{ minWidth: 30, color: selected ? "#A5B4FC" : "#64748B" }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: "0.875rem",
                          fontWeight: selected ? 600 : 500,
                          color: selected ? "#E2E8F0" : "#94A3B8",
                        }}
                      />
                      {item.soon ? (
                        <Chip
                          label="Soon"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.6rem",
                            bgcolor: "rgba(255,255,255,0.06)",
                            color: "#64748B",
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                      ) : null}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* Bottom version */}
      <Box sx={{ px: 3, py: 2 }}>
        <Typography sx={{ fontSize: "0.65rem", color: "#334155" }}>v1.0 · Beta</Typography>
      </Box>
    </Box>
  );
}

export default function AppShell({ account, onSignOut, title, actions, children }) {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const initials = (account?.displayName || account?.email || "?")
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const drawerContent = (
    <NavList
      pathname={location.pathname}
      onNavigate={isDesktop ? undefined : () => setMobileOpen(false)}
    />
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Sidebar — desktop permanent */}
      {isDesktop ? (
        <Box
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            bgcolor: "#1E293B",
            borderRight: "none",
            position: "fixed",
            top: 0,
            left: 0,
            height: "100vh",
            zIndex: 1100,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {drawerContent}
        </Box>
      ) : (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              bgcolor: "#1E293B",
              border: "none",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main content area */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          ml: isDesktop ? `${DRAWER_WIDTH}px` : 0,
        }}
      >
        {/* Top bar */}
        <AppBar position="sticky" sx={{ left: isDesktop ? DRAWER_WIDTH : 0 }}>
          <Toolbar>
            {!isDesktop ? (
              <IconButton
                edge="start"
                onClick={() => setMobileOpen(true)}
                sx={{ mr: 1, color: "text.secondary" }}
              >
                <MenuIcon />
              </IconButton>
            ) : null}

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h5" noWrap sx={{ color: "text.primary", fontWeight: 600 }}>
                {title}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
              {actions}

              {account ? (
                <>
                  <Tooltip title={account.email || ""} arrow>
                    <Box
                      onClick={(e) => setMenuAnchor(e.currentTarget)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        pl: 0.5,
                        pr: 1,
                        py: 0.5,
                        borderRadius: 2,
                        cursor: "pointer",
                        border: "1px solid transparent",
                        transition: "all 0.15s",
                        "&:hover": { bgcolor: "#F8FAFC", borderColor: "#E8EDF5" },
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 30,
                          height: 30,
                          bgcolor: "primary.main",
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          boxShadow: "0 0 0 2px rgba(91,91,214,0.2)",
                        }}
                      >
                        {initials}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary", display: { xs: "none", sm: "block" } }}>
                        {account.displayName?.split(" ")[0] || "Account"}
                      </Typography>
                      <KeyboardArrowDownIcon sx={{ fontSize: 16, color: "text.secondary", display: { xs: "none", sm: "block" } }} />
                    </Box>
                  </Tooltip>

                  <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    PaperProps={{
                      elevation: 2,
                      sx: { mt: 0.5, minWidth: 200, borderRadius: 2, overflow: "hidden" },
                    }}
                  >
                    <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
                      <Typography variant="subtitle2" sx={{ color: "text.primary" }}>
                        {account.displayName || "Account"}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {account.email}
                      </Typography>
                    </Box>
                    <MenuItem
                      onClick={() => {
                        setMenuAnchor(null);
                        if (onSignOut) onSignOut();
                      }}
                      sx={{ gap: 1.5, py: 1.25, color: "error.main", fontSize: "0.875rem" }}
                    >
                      <LogoutOutlinedIcon fontSize="small" />
                      Sign out
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Typography
                  variant="body2"
                  onClick={() => navigate("/signin")}
                  sx={{ cursor: "pointer", color: "primary.main", fontWeight: 600 }}
                >
                  Sign in
                </Typography>
              )}
            </Stack>
          </Toolbar>
        </AppBar>

        {/* Page content */}
        <Box
          component="main"
          sx={{
            flex: 1,
            p: { xs: 2.5, md: 3.5 },
            maxWidth: 1440,
            width: "100%",
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
